<script>
export default {
  name: 'HuayNumberGrid',
  props: {
    selectedNumbers: {
      type: [Array, Object],
      required: true
    },
    locked: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    numbers () {
      return Array.from({ length: 100 }, (_, index) => String(index).padStart(2, '0'))
    }
  },
  methods: {
    isSelected (number) {
      return Boolean(this.selectedNumbers[number])
    },
    select (number) {
      if (!this.locked) this.$emit('select', number)
    }
  }
}
</script>

<template>
  <div class="number-grid" :class="{ 'number-grid--locked': locked }">
    <button
      v-for="number in numbers"
      :key="number"
      class="number-button"
      :class="{ 'number-button--selected': isSelected(number) }"
      type="button"
      :disabled="locked"
      :aria-pressed="String(isSelected(number))"
      @click="select(number)"
    >
      <span class="number-button__value">{{ number }}</span>
      <span class="number-button__check" aria-hidden="true">✓</span>
    </button>
  </div>
</template>

<style scoped>
.number-grid {
  display: grid;
  grid-template-columns: repeat(10, minmax(0, 1fr));
  gap: 10px;
}

.number-button {
  position: relative;
  isolation: isolate;
  min-width: 0;
  min-height: 58px;
  border: 1px solid rgba(247, 229, 190, 0.16);
  border-radius: 14px;
  overflow: hidden;
  color: #f8ead0;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.015)),
    #191c1b;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 7px 16px rgba(0, 0, 0, 0.16);
  cursor: pointer;
  transition: transform 180ms ease, border-color 180ms ease, color 180ms ease, box-shadow 180ms ease;
  -webkit-tap-highlight-color: transparent;
}

.number-button::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 0;
  opacity: 0;
  background:
    radial-gradient(circle at 24% 12%, rgba(255, 255, 255, 0.5), transparent 34%),
    linear-gradient(145deg, #f7d684 0%, #d79a35 52%, #a96616 100%);
  transition: opacity 180ms ease;
}

.number-button:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: rgba(244, 195, 99, 0.62);
  color: #ffd981;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 10px 20px rgba(0, 0, 0, 0.24),
    0 0 0 1px rgba(244, 195, 99, 0.08);
}

.number-button:focus-visible {
  outline: 3px solid rgba(255, 214, 132, 0.4);
  outline-offset: 3px;
}

.number-button--selected {
  border-color: #ffe0a0;
  color: #241604;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    0 9px 22px rgba(208, 137, 27, 0.3),
    0 0 0 2px rgba(244, 195, 99, 0.12);
  transform: translateY(-1px);
}

.number-button--selected::before {
  opacity: 1;
}

.number-button__value {
  font-family: 'Droid Sans', sans-serif;
  font-size: clamp(0.88rem, 1.8vw, 1.08rem);
  font-weight: 700;
  letter-spacing: -0.02em;
}

.number-button__check {
  position: absolute;
  top: 4px;
  right: 5px;
  display: grid;
  place-items: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  color: #fff5db;
  background: #221406;
  font-size: 9px;
  font-weight: 700;
  opacity: 0;
  transform: scale(0.5) rotate(-12deg);
  transition: opacity 180ms ease, transform 180ms ease;
}

.number-button--selected .number-button__check {
  opacity: 1;
  transform: scale(1) rotate(0deg);
}

.number-grid--locked .number-button {
  cursor: not-allowed;
}

.number-button:disabled {
  opacity: 0.62;
}

.number-button--selected:disabled {
  opacity: 0.9;
}

@media (max-width: 767.98px) {
  .number-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 9px;
  }

  .number-button {
    aspect-ratio: 1 / 1;
    min-height: 0;
    border-radius: 13px;
  }

  .number-button__value {
    font-size: 1rem;
  }
}

@media (max-width: 374.98px) {
  .number-grid {
    gap: 7px;
  }

  .number-button {
    border-radius: 11px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .number-button,
  .number-button::before,
  .number-button__check {
    transition: none;
  }
}
</style>
